import { useCallback } from 'react';

import { getContext } from 'configs/context';

import { BATCH_MINIAPP_ID } from 'pages/Batch/configs';
import analytics from 'pages/Batch/utils/analytics';

import type { Credits } from '@picsart/miniapp-sdk-types';

import { isDev } from 'configs/constants';

import useAuthInfo from 'hooks/useAuthInfo';

import isUserAuthorized from 'utils/common/isUserAuthorized';

type UseSubscribeReturnType = {
  checkSubscriptionLimits: (creditsData: Credits, toolPrice: number) => Promise<boolean | undefined>;
  subscribe: (isStorageFull?: boolean) => Promise<void>;
  openOffer: () => Promise<void>;
  openRegistration: () => Promise<void>;
};

const useSubscribe = (): UseSubscribeReturnType => {
  const { isProOrGoldUser } = useAuthInfo();

  // Opens the platform auth screen for anonymous users (registration flow).
  const openRegistration = useCallback(async () => {
    try {
      await getContext().handlers.openAuthScreen({ action: 'signup' });
    } catch (err) {
      console.error('[offerFlow] openRegistration ← openAuthScreen failed', err);
      throw err;
    }
  }, []);

  // Opens the platform offer/subscription screen. `source`, `toolId` and
  // `requestId` are auto-injected by the SDK from the miniapp packageId.
  // The post-purchase tier update is handled reactively in useUserData via the
  // monetization.userSubscription model (see useMonetization in export-and-share).
  const openOffer = useCallback(async () => {
    try {
      await getContext().handlers.openOfferScreen({
        forceTier: 'pro',
        touchpoint: BATCH_MINIAPP_ID,
        is_pro_touchpoint: true,
        screen_id: BATCH_MINIAPP_ID,
      });
    } catch (err) {
      console.error('[offerFlow] openOffer ← openOfferScreen failed', err);
      throw err;
    }
  }, []);

  const subscribe = useCallback(
    async (isStorageFull?: boolean) => {
      const userAuthorized = isUserAuthorized();

      if (!userAuthorized) {
        await openRegistration();
        analytics.registrationOpen();
        return;
      }

      if (isProOrGoldUser && !isStorageFull) {
        return;
      }

      await openOffer();
      analytics.subscriptionOfferOpen();
    },
    [isProOrGoldUser, openOffer, openRegistration],
  );

  const checkSubscriptionLimits = useCallback(
    async (creditsData: Credits, toolPrice: number) => {
      const { areCreditsAvailable, creditsInfo } = creditsData;
      const userAuthorized = isUserAuthorized();

      if (isDev) {
        return true;
      }

      if (!userAuthorized) {
        await subscribe();
        return false;
      }

      if (!areCreditsAvailable) {
        await openOffer();
        return false;
      }

      if (creditsInfo) {
        const { credits, addonCredits } = creditsInfo;
        const availableCredits = credits + addonCredits;

        if (availableCredits >= Number(toolPrice)) {
          return true;
        }

        await openOffer();
        return false;
      }

      return true;
    },
    [openOffer, subscribe],
  );

  return {
    checkSubscriptionLimits,
    subscribe,
    openOffer,
    openRegistration,
  };
};

export default useSubscribe;
